// `detail` is a string from DRF, `{ code, message }` from the chat service, or FastAPI's list of
// validation errors (`[{ loc, msg }]`).
type ApiErrorDetail = string | { message?: string } | { msg?: string }[]
type ApiErrorBody = { detail?: ApiErrorDetail; message?: string; error?: string; errors?: string[] }
type ApiError = { response?: { data?: unknown } }

// A gateway 502 or a Django 500 page arrives as a string body, but it is markup, not a
// message anyone should be shown — fall back to the caller's text instead.
const isMarkup = (value: string) => /^\s*</.test(value)

// DRF reports errors in several shapes: a bare string or a list of strings under a field key,
// a nested serializer object one level below, a top-level array of non-field messages, or a
// whole-batch object like {"refund_errors": {"<id>": "..."}}. Flatten all of them: recurse
// through strings, arrays, and nested objects. A field value is not always an array.
const firstMessage = (value: unknown): string | undefined => {
  if (typeof value === 'string') return (!value || isMarkup(value)) ? undefined : value
  if (Array.isArray(value)) return value.map(firstMessage).find(Boolean)
  if (value && typeof value === 'object') return Object.values(value).map(firstMessage).find(Boolean)

  return undefined
}

const detailMessage = (detail: ApiErrorDetail | undefined): string | undefined => {
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) return typeof detail[0]?.msg === 'string' ? detail[0].msg : undefined
  return typeof detail?.message === 'string' ? detail.message : undefined
}

export const getApiErrorMessage = (err: unknown, fallback: string): string => {
  const data = (err as ApiError)?.response?.data

  // A raw string body: guard against markup (502/500 HTML pages) before returning it.
  if (typeof data === 'string') return (!data || isMarkup(data)) ? fallback : data
  if (Array.isArray(data)) return firstMessage(data) || fallback

  const body = (data && typeof data === 'object' ? data : {}) as ApiErrorBody
  // Prefer DRF's conventional keys, then the first message found anywhere under a field/nested key.
  // `detail` is read on its own: scanning it would surface the chat service's error code.
  const firstListed = firstMessage({ ...body, detail: undefined })

  return detailMessage(body.detail) || body.message || body.error || firstListed || fallback
}
